import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * A request for the page-level `PlaidLauncher` to open Plaid Link:
 * - `reconnect` — repair a broken Plaid item (needs an account id)
 * - `manage` — add or remove accounts on a healthy Plaid item (needs an account id)
 * - `connect` — connect a brand-new institution (no account yet)
 *
 * `reconnect` and `manage` both open Plaid in update mode, but they are not
 * interchangeable: reconnect treats the returned account list as non-authoritative so a
 * partial reauth can never deactivate anything, while manage treats it as authoritative
 * so deselecting an account removes it. Sending a manage request as a reconnect yields
 * an add-only flow whose removals silently do nothing.
 */
export type PlaidLauncherTarget =
  | { mode: 'reconnect'; accountId: number; surface?: PlaidLauncherSurface }
  | { mode: 'manage'; accountId: number; surface?: PlaidLauncherSurface }
  | { mode: 'connect'; surface?: PlaidLauncherSurface }

export type PlaidLauncherSurface = 'settings' | 'adviser'

export const DEFAULT_PLAID_SURFACE: PlaidLauncherSurface = 'settings'

interface PlaidLauncherState {
  target: PlaidLauncherTarget | null
}

const initialState: PlaidLauncherState = {
  target: null,
}

const plaidLauncherSlice = createSlice({
  name: 'plaidLauncher',
  initialState,
  reducers: {
    setPlaidTarget: (
      state,
      { payload }: PayloadAction<PlaidLauncherTarget>
    ) => {
      state.target = payload
    },
    clearPlaidTarget: (state) => {
      state.target = null
    },
  },
})

export const { setPlaidTarget, clearPlaidTarget } = plaidLauncherSlice.actions

export default plaidLauncherSlice.reducer
